import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  DownloadOutlined,
  FileOutlined,
  HeartOutlined,
  PaperClipOutlined,
  SendOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { Button, Empty, Image, Input, Popover, Spin, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/Store";
import {
  makeTempMessageId,
  removeChatMessage,
  useChatMessages,
  useMarkChatRead,
  useSendChatAttachment,
  useSendChatMessage,
} from "../../../../hooks/useChat";
import { ChatAttachmentDto, ChatMessageDto } from "../../../../models/chat/ChatMessageDto";
import { useOnlineUsers } from "../../../../hooks/usePresence";
import { CHAT_EMOJIS, CHAT_STICKERS, getStickerEmoji } from "../../../../utils/chatStickers";
import { linkify } from "../../../../utils/linkify";
import { chatService } from "../../../../services/ChatService";
import { openChatDock } from "./ChatEvents";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { getProfileName, useChatUserProfiles } from "./useChatUserProfiles";

const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;
const CHAT_POPUP_Z_INDEX = 1600;

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

interface DirectChatWindowProps {
  partnerUserId: string;
  partnerName?: string;
  minimized: boolean;
  onToggleMinimize: () => void;
  onHeaderPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
}

export const DirectChatWindow = memo(({ partnerUserId, partnerName, minimized, onToggleMinimize, onHeaderPointerDown }: DirectChatWindowProps) => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  const [content, setContent] = useState("");
  const [stickerOpen, setStickerOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollSnapshotRef = useRef<{ height: number; top: number } | null>(null);
  const skipNextAutoScrollRef = useRef(false);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useChatMessages(partnerUserId, true);
  const sendMessage = useSendChatMessage();
  const sendAttachment = useSendChatAttachment();
  const markRead = useMarkChatRead();
  const onlineUsers = useOnlineUsers(!minimized);
  const partnerOnline = onlineUsers.has(partnerUserId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Giữ File theo tempId để "Gửi lại" tin đính kèm bị lỗi (File không nằm trong DTO/cache).
  const attachmentFilesRef = useRef<Map<string, File[]>>(new Map());

  const messages = useMemo(() => data?.pages.flat() ?? [], [data]);
  const ordered = useMemo(() => [...messages].reverse(), [messages]);
  const latestId = messages[0]?.id;
  const profileByUserId = useChatUserProfiles([myUserId, partnerUserId]);
  const partnerProfile = profileByUserId.get(partnerUserId);

  useEffect(() => {
    if (!minimized) markRead.mutate(partnerUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized, partnerUserId]);

  useLayoutEffect(() => {
    const snapshot = scrollSnapshotRef.current;
    const el = containerRef.current;
    if (!snapshot || !el) return;
    el.scrollTop = el.scrollHeight - snapshot.height + snapshot.top;
    scrollSnapshotRef.current = null;
    skipNextAutoScrollRef.current = true;
  }, [ordered.length]);

  useEffect(() => {
    if (minimized || ordered.length === 0 || scrollSnapshotRef.current) return;
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }
    const scrollToLatest = () => {
      const el = containerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    const frame = window.requestAnimationFrame(scrollToLatest);
    const timer = window.setTimeout(scrollToLatest, 180);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [latestId, minimized, isLoading, ordered.length]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || el.scrollTop > 40 || !hasNextPage || isFetchingNextPage) return;
    scrollSnapshotRef.current = { height: el.scrollHeight, top: el.scrollTop };
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Không chặn theo isPending để mạng chậm vẫn gửi được nhiều tin liên tiếp (mỗi tin 1 tempId riêng).
  const send = () => {
    const text = content.trim();
    if (!text) return;
    sendMessage.mutate({ recipientUserId: partnerUserId, content: text, tempId: makeTempMessageId() });
    setContent("");
  };

  const pickFiles = () => fileInputRef.current?.click();

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // cho phép chọn lại cùng file
    if (!files.length) return;
    if (files.some((f) => f.size > MAX_ATTACHMENT_SIZE)) {
      message.error("Mỗi tệp tối đa 50MB");
      return;
    }
    sendFiles(files, content.trim() || undefined);
    setContent("");
  };

  const sendFiles = (files: File[], caption?: string) => {
    const tempId = makeTempMessageId();
    attachmentFilesRef.current.set(tempId, files);
    sendAttachment.mutate(
      { recipientUserId: partnerUserId, files, content: caption, tempId },
      { onSuccess: () => attachmentFilesRef.current.delete(tempId) },
    );
  };

  const sendSticker = (code: string) => {
    setStickerOpen(false);
    sendMessage.mutate({
      recipientUserId: partnerUserId,
      content: code,
      messageType: "STICKER",
      tempId: makeTempMessageId(),
    });
  };

  // Gửi lại tin lỗi: bỏ tin lỗi cũ rồi gửi mới.
  const retry = (m: ChatMessageDto) => {
    if (m.messageType === "ATTACHMENT") {
      const files = attachmentFilesRef.current.get(m.id);
      attachmentFilesRef.current.delete(m.id);
      removeChatMessage(partnerUserId, m.id);
      if (!files || files.length === 0) {
        message.info("Vui lòng chọn lại tệp để gửi");
        return;
      }
      sendFiles(files, m.content || undefined);
      return;
    }
    removeChatMessage(partnerUserId, m.id);
    sendMessage.mutate({
      recipientUserId: partnerUserId,
      content: m.content,
      messageType: m.messageType,
      tempId: makeTempMessageId(),
    });
  };

  // Chèn emoji vào ô đang gõ -> gửi chung với chữ. Giữ popover mở để chèn nhiều.
  const insertEmoji = (emoji: string) => {
    setContent((prev) => (prev.length >= 2000 ? prev : prev + emoji));
  };

  const renderAttachments = (m: ChatMessageDto, mine: boolean) => {
    const atts: ChatAttachmentDto[] = m.attachments ?? [];
    const images = atts.filter((a) => a.isImage && a.url);
    const files = atts.filter((a) => !a.isImage);
    const sending = m.status === "sending";
    return (
      <div className="flex flex-col gap-1">
        {images.length > 0 && (
          <Image.PreviewGroup>
            <div className="flex flex-wrap gap-1">
              {images.map((a) => (
                <Image
                  key={a.id}
                  src={a.url ?? undefined}
                  alt={a.originalFileName}
                  width={132}
                  height={132}
                  className="rounded-[14px]"
                  style={{ objectFit: "cover" }}
                  preview={!sending}
                />
              ))}
            </div>
          </Image.PreviewGroup>
        )}
        {files.map((a) => (
          <button
            key={a.id}
            type="button"
            disabled={sending}
            onClick={() => chatService.downloadChatFile(a.id, a.originalFileName)}
            className={[
              "flex max-w-[260px] cursor-pointer items-center gap-2 rounded-[18px] border px-3 py-2 text-sm shadow-sm disabled:cursor-default disabled:opacity-70",
              mine ? "border-[#b9d7ff] bg-white/95 text-[#10243f] hover:bg-white" : "border-white bg-white text-neutral-800 hover:bg-neutral-50",
            ].join(" ")}
          >
            <FileOutlined className="shrink-0 text-blue-500" />
            <span className="truncate flex-1 text-left">{a.originalFileName}</span>
            <span className="shrink-0 text-[11px] text-neutral-400">{formatSize(a.fileSize)}</span>
            {!sending && <DownloadOutlined className="shrink-0 text-neutral-400" />}
          </button>
        ))}
        {m.content && (
          <div
            className={[
              "rounded-[18px] px-3 py-1.5 text-sm leading-5 whitespace-pre-wrap break-words shadow-sm",
              mine ? "bg-[#d9ecff] text-[#10243f]" : "bg-white text-[#17212f]",
            ].join(" ")}
          >
            {linkify(m.content)}
          </div>
        )}
      </div>
    );
  };

  const emojiPicker = (
    <div className="grid grid-cols-8 gap-0.5 w-64">
      {CHAT_EMOJIS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => insertEmoji(e)}
          className="text-xl leading-none p-1 rounded bg-transparent border-none cursor-pointer hover:bg-neutral-100 transition"
        >
          {e}
        </button>
      ))}
    </div>
  );

  const stickerPicker = (
    <div className="grid grid-cols-4 gap-1 w-52">
      {CHAT_STICKERS.map((s) => (
        <button
          key={s.code}
          type="button"
          title={s.label}
          onClick={() => sendSticker(s.code)}
          className="text-2xl leading-none p-1.5 rounded-lg bg-transparent border-none cursor-pointer hover:bg-neutral-100 transition"
        >
          {s.emoji}
        </button>
      ))}
    </div>
  );

  const header = (
    <div
      className="flex items-center justify-between gap-2 rounded-t-[18px] bg-[#0068ff] px-3.5 py-2.5 text-white shadow-[0_2px_12px_rgba(0,104,255,0.24)] cursor-pointer select-none"
      style={{ touchAction: "none" }}
      onPointerDown={onHeaderPointerDown}
      onClick={onToggleMinimize}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="relative shrink-0">
          <CommonAvatar size={34} src={partnerProfile?.avatarId} name={getProfileName(partnerProfile, partnerName)} className="bg-white text-[#0068ff]" />
          {partnerOnline && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#0068ff] bg-[#22c55e]" />}
        </div>
        <div className="min-w-0 leading-tight">
          <span className="font-medium text-sm truncate block">{getProfileName(partnerProfile, partnerName || partnerUserId)}</span>
          <span className="text-[11px] text-white/80">{partnerOnline ? "Đang hoạt động" : "Ngoại tuyến"}</span>
        </div>
      </div>
    </div>
  );

  if (minimized) {
    return <div className="w-72 max-w-[calc(100vw-16px)] rounded-[18px] shadow-lg">{header}</div>;
  }

  return (
    <div className="flex h-[560px] max-h-[calc(100vh-28px)] w-[380px] max-w-[calc(100vw-16px)] transform-gpu flex-col overflow-hidden rounded-[18px] border border-[#d9e4f2] bg-white shadow-[0_18px_48px_rgba(15,23,42,0.22)] sm:max-h-[76vh]">
      {header}

      <div ref={containerRef} onScroll={handleScroll} className="relative flex-1 overflow-y-auto bg-[#eaf1fb] px-3.5 py-3">
        {(isLoading || isFetchingNextPage) && (
          <div className="pointer-events-none sticky top-0 z-10 flex justify-center py-1">
            <span className="rounded-full bg-white/85 px-2 py-1 shadow-sm backdrop-blur">
              <Spin size="small" />
            </span>
          </div>
        )}
        {!isLoading && ordered.length === 0 && (
          <div className="flex h-full min-h-[320px] items-center justify-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có tin nhắn" />
          </div>
        )}
        {ordered.length > 0 && (
          <div className="w-full">
            {ordered.map((m, index) => {
              const prev = ordered[index - 1];
              const next = ordered[index + 1];
              const mine = m.senderUserId === myUserId;
              const isSticker = m.messageType === "STICKER";
              const isAttachment = m.messageType === "ATTACHMENT";
              const senderProfile = profileByUserId.get(m.senderUserId);
              const senderName = getProfileName(senderProfile, mine ? "You" : partnerName || m.senderUserId);
              const groupedWithPrev = prev?.senderUserId === m.senderUserId;
              const groupedWithNext = next?.senderUserId === m.senderUserId;
              const showSenderAvatar = !mine && !groupedWithNext;
              const bubbleShape = mine
                ? groupedWithNext
                  ? "rounded-[18px] rounded-br-md"
                  : "rounded-[18px] rounded-br-[6px]"
                : groupedWithNext
                  ? "rounded-[18px] rounded-bl-md"
                  : "rounded-[18px] rounded-bl-[6px]";
              return (
                <div key={m.id}>
                  <div
                    key={m.id}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      openChatDock();
                    }}
                    className={`flex items-end gap-2 ${groupedWithPrev ? "mt-1" : "mt-3"} ${mine ? "justify-end" : "justify-start"}`}
                  >
                    {!mine && (
                      <div className="w-7 shrink-0">
                        {showSenderAvatar && (
                          <CommonAvatar size={28} src={senderProfile?.avatarId} name={senderName} className="bg-white text-[#0068ff] shadow-sm" />
                        )}
                      </div>
                    )}
                    <div className={`flex max-w-[78%] flex-col ${mine ? "items-end" : "items-start"} ${m.status === "sending" ? "opacity-70" : ""}`}>
                      {isSticker ? (
                        <div className="select-none text-5xl leading-none drop-shadow-sm" title={m.registeredTime}>
                          {getStickerEmoji(m.content) ?? "❓"}
                        </div>
                      ) : isAttachment ? (
                        <div title={m.registeredTime}>{renderAttachments(m, mine)}</div>
                      ) : (
                        <div
                          className={`px-3 py-2 text-[14px] leading-5 whitespace-pre-wrap break-words shadow-sm ${bubbleShape} ${
                            mine ? "bg-[#d9ecff] text-[#10243f]" : "bg-white text-[#17212f]"
                          }`}
                          title={m.registeredTime}
                        >
                          {linkify(m.content)}
                        </div>
                      )}
                      {m.registeredTime && (
                        <div className={`mt-0.5 px-1 text-[10px] text-neutral-400 ${mine ? "text-right" : "text-left"}`}>{m.registeredTime}</div>
                      )}
                      {mine && m.status && (
                        <div className="text-[10px] mt-0.5 pr-1">
                          {m.status === "sending" ? (
                            <span className="text-neutral-400">Đang gửi...</span>
                          ) : (
                            <span className="text-red-500 cursor-pointer hover:underline" onClick={() => retry(m)}>
                              Gửi lỗi - Gửi lại
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-end gap-1.5 border-t border-[#dbe5f0] bg-white px-2.5 py-2.5">
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilesSelected} />
        <Tooltip title="Đính kèm tệp">
          <Button
            type="text"
            icon={<PaperClipOutlined />}
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
            loading={sendAttachment.isPending}
            onClick={pickFiles}
          />
        </Tooltip>
        <Popover
          content={emojiPicker}
          trigger="click"
          open={emojiOpen}
          onOpenChange={(open) => {
            setEmojiOpen(open);
            if (open) setStickerOpen(false);
          }}
          placement="topLeft"
          zIndex={CHAT_POPUP_Z_INDEX}
        >
          <Button
            type="text"
            title="Emoji"
            icon={<SmileOutlined />}
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
          />
        </Popover>
        <Popover
          content={stickerPicker}
          trigger="click"
          open={stickerOpen}
          onOpenChange={(open) => {
            setStickerOpen(open);
            if (open) setEmojiOpen(false);
          }}
          placement="topLeft"
          zIndex={CHAT_POPUP_Z_INDEX}
        >
          <Button
            type="text"
            title="Nhãn dán"
            icon={<HeartOutlined />}
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
          />
        </Popover>
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={2000}
          placeholder="Nhập tin nhắn..."
          className="!rounded-[20px] !border-none !bg-[#f2f4f8] !px-3 !py-2 !text-[14px] !leading-5 !shadow-none focus:!bg-white"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined className="text-white" />}
          disabled={!content.trim()}
          loading={sendMessage.isPending}
          className="!h-9 !w-9 !min-w-9 !bg-[#0068ff]"
          onClick={send}
        />
      </div>
    </div>
  );
});
